<template>
  <b-modal
    id="them-thiet-bi-qr"
    ref="themThietBiQr"
    title="Thêm thiết bị xác thực"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    @hidden="hideModal"
  >
    <div class="mb-1 them-thiet-bi-qr otp-scope">
      <div class="title font-weight-bold">Thêm thiết bị dự phòng bằng mã QR</div>
      <div class="sub-title mt-1 fs-14px">
        Dùng ứng dụng eDoc trên Mobile/Tablet quét mã QR để thêm thiết bị xác thực 2 bước cho tài khoản của bạn.
      </div>

      <!-- Bước 1 (chỉ đường ADD, user >=1 thiết bị Active): xác thực OTP thiết bị đang dùng để ủy quyền
           (chống người lạ tự thêm thiết bị). Đường ENROLL (0-factor) BỎ bước này -> mintTicket thẳng. -->
      <div v-if="branch === 'add' && !ticketId && !pending" class="content mt-2">
        <p class="mb-1">
          Nhập mã OTP hiện tại trên <b>một thiết bị bạn đang dùng</b> để xác nhận thêm thiết bị mới.
        </p>
        <b-form-input
          v-otp-autofocus
          v-model="authorizerOtp"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="off"
          pattern="[0-9]*"
          class="outline-primary input"
          placeholder="Mã OTP 6 số"
          @keyup.enter="mintTicket"
        />
        <div class="d-flex mt-2">
          <base-button
            button="Tiếp tục"
            :disabled="isLoading || !authorizerOtp"
            @click="mintTicket"
          />
        </div>
      </div>

      <!-- Bước 2: hiện QR cho thiết bị mới quét. Poll pending mỗi 3s. -->
      <div v-if="ticketId && !pending" class="content mt-2 text-center">
        <p class="mb-1">
          Mở <b>ứng dụng eDoc Mobile</b> - chọn <b>Kích hoạt bằng QR</b> - quét mã dưới đây:
        </p>
        <div class="qr-box">
          <qrcode-vue :value="qrValue" :size="220" level="M" />
        </div>
        <p class="mt-1 text-muted fs-14px">
          Mã QR còn hiệu lực trong <b>{{ remaining }}</b> giây.
        </p>
        <b-button
          v-if="remaining <= 0"
          variant="outline-primary"
          size="sm"
          @click="mintTicket"
        >Tạo mã QR mới</b-button>
      </div>

      <!-- Bước 3: app đã quét -> xác nhận binding -> Active. -->
      <div v-if="pending" class="content mt-2">
        <p class="mb-1">
          Thiết bị <b>{{ pending.tenThietBi }}</b> vừa quét mã QR. Nếu đúng là bạn,
          hãy xác nhận để kích hoạt thiết bị này.
        </p>
        <div class="d-flex">
          <base-button
            button="Xác nhận kích hoạt thiết bị này"
            :disabled="isLoading"
            @click="confirmBinding"
          />
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <div class="w-100 text-right">
        <b-button variant="outline-primary" class="ml-1" @click="hideModal()">Đóng</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
  import QrcodeVue from 'qrcode.vue'
  import { DOMAIN, API_VERSION } from '@/constants/constants'

  // UC7.4 port QTHT (plan 260824-2156): thêm thiết bị dự phòng qua QR handoff, THAY register-device cổ điển.
  // Branch (H-1 red-team): user >=1 thiết bị Active -> đường ADD (gate OTP thiết bị cũ, mintAddDeviceTicket);
  // user 0-factor -> đường ENROLL (không OTP, mintEnrollTicket đã có). Kết thúc ở confirmBinding -> Active
  // (KHÔNG re-login như QrEnrollModal login). Reload danh sách qua @added.
  export default {
    name: 'ModalThemThietBiQr',
    components: { QrcodeVue },
    data() {
      return {
        branch: 'add', // 'add' (>=1 Active) | 'enroll' (0-factor) - set khi showModal theo danh sách device.
        authorizerOtp: null,
        ticketId: null,
        remaining: 0,
        pending: null,
        isLoading: false,
        pollTimer: null,
        countdownTimer: null,
      }
    },
    computed: {
      // Nội dung QR: {t: ticketId, u: common-be public base URL KÈM /api}. DÙNG CÙNG hằng QrEnrollModal
      // (DOMAIN + API_VERSION) chống URL drift; app allowlist host (apicommon.nso.gov.vn ở PROD).
      qrValue() {
        return JSON.stringify({ t: this.ticketId, u: DOMAIN + API_VERSION })
      },
    },
    methods: {
      resetData() {
        this.authorizerOtp = null
        this.ticketId = null
        this.remaining = 0
        this.pending = null
        this.isLoading = false
        this.stopTimers()
      },
      stopTimers() {
        if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
        if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
      },
      errMessage(payload) {
        const d = payload && (payload.data || payload)
        return (d && (d.message || d.Message)) || ''
      },
      // Branch ADD: gate OTP thiết bị cũ -> mintAddDeviceTicket. Branch ENROLL: mintEnrollTicket (không OTP).
      // authorizerDeviceId null -> BE fan-out verify mọi thiết bị Active.
      mintTicket() {
        if (this.branch === 'add' && !this.authorizerOtp) return
        this.isLoading = true
        this.pending = null
        const dispatch = this.branch === 'add'
          ? this.$store.dispatch('common/mintAddDeviceTicket', { authorizerOtp: this.authorizerOtp, authorizerDeviceId: null })
          : this.$store.dispatch('common/mintEnrollTicket')
        dispatch
            .then((res) => {
              this.isLoading = false
              // common-fe action resolve response.data (ServiceResponse camelCase {data, succeeded, message}).
              if (res && res.succeeded && res.data) {
                this.ticketId = res.data.ticketId
                this.remaining = res.data.expiresInSeconds || 180
                this.startCountdown()
                this.startPolling()
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Không tạo được mã QR. Vui lòng thử lại.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              // Gate OTP fail / đạt max thiết bị -> hiện message BE, clear ô OTP (chống auto-verify mã cũ).
              this.authorizerOtp = null
              this.$toasted.global.showErrorMessage({ message: this.errMessage(err) || 'Không tạo được mã QR. Vui lòng thử lại.' })
            })
      },
      startCountdown() {
        this.stopCountdown()
        this.countdownTimer = setInterval(() => {
          if (this.remaining > 0) this.remaining -= 1
          else this.stopCountdown()
        }, 1000)
      },
      stopCountdown() {
        if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
      },
      startPolling() {
        if (this.pollTimer) clearInterval(this.pollTimer)
        this.pollTimer = setInterval(() => this.pollPending(), 3000)
      },
      pollPending() {
        if (this.pending) return
        this.$store.dispatch('common/getPendingEnroll')
            .then((res) => {
              if (res && res.data && res.data.hasPending) {
                this.pending = { deviceId: res.data.deviceId, tenThietBi: res.data.tenThietBi }
                this.stopTimers() // đã có pending -> dừng poll + countdown
              }
            })
            .catch(() => { /* poll best-effort */ })
      },
      confirmBinding() {
        if (!this.pending) return
        this.isLoading = true
        this.$store.dispatch('common/confirmEnrollBinding', { deviceId: this.pending.deviceId })
            .then((res) => {
              this.isLoading = false
              if (res && res.succeeded) {
                this.$toasted.global.showSuccessMessage({ message: 'Đã thêm thiết bị mới thành công.' })
                this.$emit('added')
                this.hideModal()
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Xác nhận thất bại. Vui lòng thử lại.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              // action confirmEnrollBinding reject bằng error.response -> ở đây err ĐÃ là {data:{message}}.
              // KHÔNG deref .response lần nữa (khớp catch mintTicket cùng file) để giữ message BE (vd channel-binding).
              this.$toasted.global.showErrorMessage({ message: this.errMessage(err) || 'Xác nhận thất bại. Vui lòng thử lại.' })
            })
      },
      // devices: danh sách thiết bị hiện tại (từ màn cha) -> quyết định branch add vs enroll.
      async showModal(devices) {
        this.resetData()
        const hasActive = Array.isArray(devices) && devices.some((d) => d.state === 1)
        this.branch = hasActive ? 'add' : 'enroll'
        await this.$refs.themThietBiQr.show()
        // Đường enroll (0-factor): không cần OTP -> mint QR ngay. Đường add: chờ user nhập OTP rồi bấm Tiếp tục.
        if (this.branch === 'enroll') this.mintTicket()
      },
      hideModal() {
        this.stopTimers()
        this.$refs.themThietBiQr.hide()
        this.resetData()
      },
    },
    beforeDestroy() {
      this.stopTimers()
    },
  }
</script>

<style lang="scss" scoped>
  // Reskin OTP: overlay token đồng bộ QrEnrollModal (cùng repo). QR nền trắng luôn (máy quét đọc kể cả dark-mode).
  .them-thiet-bi-qr {
    font-family: var(--otp-sans);
    color: var(--otp-ink);
    .title { font-size: 16px; font-family: var(--otp-display); color: var(--otp-ink); }
    .fs-14px { font-size: 14px; }
    .mt-1 { margin-top: 1rem !important; }
    .mt-2 { margin-top: 1rem !important; }
    .mb-1 { margin-bottom: 1rem !important; }
    .ml-1 { margin-left: 1rem !important; }
    .qr-box {
      display: inline-block;
      padding: 14px;
      background: #fff;
      border: 1px solid var(--otp-border-strong);
      border-radius: var(--otp-radius);
      box-shadow: var(--otp-shadow-sm);
    }
    .text-muted { color: var(--otp-muted); }
    .input {
      border: 1px solid var(--otp-border-strong);
      border-radius: var(--otp-radius-sm);
      height: 36px;
      max-width: 50%;
      &:focus { border-color: var(--otp-blue); box-shadow: var(--otp-ring); outline: none; }
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      &[type='number'] { -moz-appearance: textfield; }
    }
  }
</style>
